// The WebAssembly interface of crates/psim-wasm, shared by the worklet host
// (worklet.ts) and the main-thread pose instance (pose-instance.ts).
//
// The exports are numeric C-ABI functions with no imports (crates/psim-wasm/src/lib.rs):
// text goes in as UTF-8 bytes written into memory from psim_alloc, and comes out as a
// pointer and a length. The worklet scope has no TextEncoder or TextDecoder, so the
// UTF-8 conversion here is done by hand. Everything here runs in the worklet scope.

import type { Powertrain, TimelineEvent } from "./protocol";

/** The psim_abi_version this page speaks. */
export const WASM_ABI_VERSION = 2;

/** psim_new's first argument: what the spec text is. */
export const KIND_JOB = 0;
export const KIND_LIVE = 1;

/** psim_asset's first argument: an impulse response, a mono WAV file whose id is its path relative to data/ir/. */
export const ASSET_IR = 0;

/** Values in the state buffer before the slow state: frame, cycle, θ, ω. */
export const STATE_FIELDS = 4;
export const S_FRAME = 0;
export const S_CYCLE = 1;
export const S_THETA = 2;
export const S_OMEGA = 3;

/** How many slow-state values psim_slow_in_ptr holds. */
export const SLOW_IN = 16;

/** psim_set_control's answers. */
export const CONTROL_OK = 0;
export const CONTROL_UNKNOWN = 1;
export const CONTROL_OUT_OF_RANGE = 2;

/** The exports of psim_wasm.wasm. Pointers are byte offsets into memory. */
export interface PsimExports {
  memory: WebAssembly.Memory;
  psim_abi_version(): number;
  psim_alloc(len: number): number;
  psim_dealloc(ptr: number, len: number): void;
  psim_asset(kind: number, idPtr: number, idLen: number, ptr: number, len: number): number;
  psim_new(kind: number, specPtr: number, specLen: number, bundlePtr: number, bundleLen: number): number;
  psim_error_ptr(): number;
  psim_error_len(): number;
  psim_free(host: number): void;
  psim_info_ptr(host: number): number;
  psim_info_len(host: number): number;
  psim_max_frames(): number;
  psim_render(host: number, frames: number): number;
  psim_out_ptr(host: number): number;
  psim_set_control(host: number, index: number, value: number): number;
  psim_state_ptr(host: number): number;
  psim_slow_in_ptr(host: number): number;
  psim_pose_at(host: number, theta: number): number;
  psim_trap(): void;
}

/** One control of a voice, as its info lists it. */
export interface VoiceControl {
  id: string;
  unit: string;
  min: number;
  max: number;
  default: number;
}

/** What a voice runs (crates/psim-wasm/src/host.rs, Info). */
export interface VoiceInfo {
  abi: number;
  job: string;
  module: string;
  name: string;
  tier: string;
  host_tier: string;
  sample_rate: number;
  frames: number | null;
  max_frames: number;
  latency_frames: number;
  outputs: string[];
  controls: VoiceControl[];
  slow: string[];
  pose: string[];
  /** The listener control's choices, by index: "direct", then the engine's positions. */
  listeners: string[];
  engine: string | null;
}

/** An asset the voice reads when it is built: an impulse response's WAV bytes and its id. */
export interface VoiceAsset {
  id: string;
  bytes: Uint8Array;
}

/** The live spec psim_new reads with KIND_LIVE (crates/psim-wasm/src/live.rs). */
export interface LiveSpec {
  source: string;
  tier?: string;
  sample_rate: number;
  load: { kind: string; params: Record<string, number> };
  timeline: TimelineEvent[];
}

export function liveSpec(powertrain: Powertrain, timeline: TimelineEvent[] | undefined, sampleRate: number): LiveSpec {
  return {
    source: powertrain.source,
    sample_rate: sampleRate,
    load: { kind: powertrain.load.kind, params: powertrain.load.params },
    timeline: timeline ?? [],
  };
}

/** UTF-8 bytes of a string, without TextEncoder. */
export function utf8Encode(text: string): Uint8Array {
  const out: number[] = [];
  for (const ch of text) {
    const c = ch.codePointAt(0)!;
    if (c < 0x80) out.push(c);
    else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 63));
    else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
    else out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
  }
  return Uint8Array.from(out);
}

/** A string from UTF-8 bytes, without TextDecoder. The bytes come from Rust, so they are valid UTF-8. */
export function utf8Decode(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; ) {
    const b = bytes[i]!;
    let c: number;
    if (b < 0x80) {
      c = b;
      i += 1;
    } else if (b < 0xe0) {
      c = ((b & 31) << 6) | (bytes[i + 1]! & 63);
      i += 2;
    } else if (b < 0xf0) {
      c = ((b & 15) << 12) | ((bytes[i + 1]! & 63) << 6) | (bytes[i + 2]! & 63);
      i += 3;
    } else {
      c = ((b & 7) << 18) | ((bytes[i + 1]! & 63) << 12) | ((bytes[i + 2]! & 63) << 6) | (bytes[i + 3]! & 63);
      i += 4;
    }
    s += String.fromCodePoint(c);
  }
  return s;
}

/** Text the module wrote at ptr, len bytes long. */
export function readText(ex: PsimExports, ptr: number, len: number): string {
  return utf8Decode(new Uint8Array(ex.memory.buffer, ptr, len));
}

/** Synchronous instantiation from bytes (Safari before 16.4 cannot receive a compiled module). */
export function instantiate(bytes: BufferSource): PsimExports {
  const instance = new WebAssembly.Instance(new WebAssembly.Module(bytes), {});
  return checkAbi(instance.exports as unknown as PsimExports);
}

export function checkAbi(ex: PsimExports): PsimExports {
  const abi = typeof ex.psim_abi_version === "function" ? ex.psim_abi_version() : -1;
  if (abi !== WASM_ABI_VERSION) throw new Error(`the voice's wasm speaks ABI ${abi}, the page ${WASM_ABI_VERSION}`);
  return ex;
}

/**
 * Builds a voice from a spec (a job file, or a live spec) and an engine bundle, both
 * UTF-8 bytes, and the impulse responses of the engine's listeners
 * (docs/contracts/listeners.md). Returns its handle and info, or throws with the
 * reason Rust gave.
 */
export function newVoice(
  ex: PsimExports,
  kind: number,
  spec: Uint8Array,
  bundle: Uint8Array,
  irs: readonly VoiceAsset[] = [],
): { handle: number; info: VoiceInfo } {
  const put = (bytes: Uint8Array): number => {
    if (bytes.length === 0) return 0;
    const ptr = ex.psim_alloc(bytes.length);
    new Uint8Array(ex.memory.buffer, ptr, bytes.length).set(bytes);
    return ptr;
  };
  // psim_asset copies each one; psim_new takes all of them.
  for (const ir of irs) {
    const id = utf8Encode(ir.id);
    const ip = put(id);
    const bp = put(ir.bytes);
    const r = ex.psim_asset(ASSET_IR, ip, id.length, bp, ir.bytes.length);
    if (ip !== 0) ex.psim_dealloc(ip, id.length);
    if (bp !== 0) ex.psim_dealloc(bp, ir.bytes.length);
    if (r !== 0) throw new Error(`the voice did not take the asset ${ir.id} (${r})`);
  }
  const sp = put(spec);
  const bp = put(bundle);
  const handle = ex.psim_new(kind, sp, spec.length, bp, bundle.length);
  if (sp !== 0) ex.psim_dealloc(sp, spec.length);
  if (bp !== 0) ex.psim_dealloc(bp, bundle.length);
  if (handle === 0) throw new Error(readText(ex, ex.psim_error_ptr(), ex.psim_error_len()));
  const info = JSON.parse(readText(ex, ex.psim_info_ptr(handle), ex.psim_info_len(handle))) as VoiceInfo;
  return { handle, info };
}
